import { Outlet } from "react-router-dom";

import { NAVBAR_HEIGHT } from "../../components/Navbar/Navbar";
import ErrorState from "../../components/ui/ErrorState";
import Spinner from "../../components/ui/Spinner";
import { useViewer, can } from "../../services/viewer";

/**
 * The shell for the admin section.
 *
 * Mirrors InsightsLayout's page frame -- navbar offset, max-width container, dark
 * surface -- and deliberately not the rest of it. No date range, no search, no
 * buildings fetch: none of this section's pages are time-windowed, and loading the
 * whole portfolio to show a list of people would be work nobody asked for.
 *
 * It gates on `ManagePeople` so a viewer who reaches the URL directly is told why
 * rather than shown an empty page that looks broken. That is a courtesy, not the
 * boundary: every endpoint behind these pages is admin-gated server-side, so a
 * viewer who bypasses this gets 403s instead of data.
 */
export default function AdminLayout() {
  const { data: viewer, isPending, isError, refetch } = useViewer();

  if (isPending) {
    return (
      <Shell>
        <div className="flex justify-center py-16">
          <Spinner size={96} />
        </div>
      </Shell>
    );
  }

  if (isError) {
    return (
      <Shell>
        <ErrorState
          title="We couldn't check your permissions"
          message="Reload to try again. If this keeps happening, your session may have expired."
          onRetry={() => refetch()}
        />
      </Shell>
    );
  }

  if (!can(viewer, "ManagePeople")) {
    return (
      <Shell>
        <ErrorState
          title="You don't have access to this section"
          message={
            "Managing people is limited to organization administrators. " +
            "Ask an administrator in your organization if you need access."
          }
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <Outlet context={{ viewer }} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main
      className="min-h-screen bg-surface text-white pb-24"
      style={{ paddingTop: NAVBAR_HEIGHT + 24 }}
    >
      <div className="max-w-screen-2xl mx-auto px-6">{children}</div>
    </main>
  );
}
